/* eslint-disable no-console */
"use client";
import React, { Fragment, useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { ExclamationCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import { Col, Modal, Row } from "antd";
import Cookies from "js-cookie";
import { NumericFormat } from "react-number-format";

import AdBlock from "@/component/addBlock/page";
import Calculation from "@/component/calculation/page";
import CompanyHistory from "@/component/companyHistory/page";
import News from "@/component/news/page";
import Projection from "@/component/projection/page";
import Spinner from "@/component/spinner";
import StockCard from "@/component/stockCard/page";
import Header from "@/component/stockHeader/page";
import SummaryChart from "@/component/SummaryChart/page";
import VideoModule from "@/component/vidioModule/page";
import {
  GET_BUYING_OPPORTUNITIES,
  GET_OPENAI_RESPONSE,
  GET_PROJECTION,
  GET_STOCK_NEWS,
  SIMILAR_STOCK,
  GET_BANNERS,
} from "@/graphql/query/stocks";
import { CONTENT_DATA, ROUTE, URLS } from "@/lib/constants";
import { CurrencySymbol } from "@/lib/currencySymbol";
import tickerNameFormat from "@/lib/tickerNameFormat";
import { TOOLTIPS } from "@/lib/tooltip";

const StockClient = ({ params, stockData, stockDetailError, setOpen }) => {
  const paramsValue =
    typeof params.value === "string"
      ? JSON.parse(params?.value)
      : params?.value;
  const { name, exchange } = paramsValue;
  const router = useRouter();
  const xToken = Cookies.get("x-token");
  const id = Cookies.get("id");

  // Separate data states
  const [stockAllData, setStockAllData] = useState({
    stockNews: null,
    similarStocks: null,
    openAiResponse: null,
    projection: null,
    buyingOpportunitiesData: null,
    addBanners: null,
  });

  const [errorModal, setErrorModal] = useState(false);

  // Individual loading states
  const [loadingStates, setLoadingStates] = useState({
    stockNews: true,
    similarStocks: true,
    openAiResponse: true,
    projection: true,
    buyingOpportunities: true,
    addBanners: true,
  });

  // Lazy queries
  const [fetchNews] = useLazyQuery(GET_STOCK_NEWS);
  const [fetchSimilar] = useLazyQuery(SIMILAR_STOCK);
  const [fetchAi] = useLazyQuery(GET_OPENAI_RESPONSE);
  const [fetchProjection] = useLazyQuery(GET_PROJECTION);
  const [fetchOpportunities] = useLazyQuery(GET_BUYING_OPPORTUNITIES);
  const [fetchBanners] = useLazyQuery(GET_BANNERS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (stockDetailError) {
      setOpen && setOpen(false);
      setErrorModal(true);
    }
  }, [stockDetailError]);

  // Helper function to update loading state
  const updateLoadingState = (key, isLoading) => {
    setLoadingStates((prev) => ({
      ...prev,
      [key]: isLoading,
    }));
  };

  // Helper function to update data state
  const updateDataState = (key, data) => {
    setStockAllData((prev) => ({
      ...prev,
      [key]: data,
    }));
  };

  useEffect(() => {
    if (!name || !exchange) return;

    const NAME = name?.toUpperCase();
    const EXCHANGE = exchange?.toUpperCase();
    const input = {
      stockId: NAME,
      exchange: EXCHANGE,
    };

    const context = {
      headers: {
        "x-visitor-token": id,
      },
    };

    // Fetch News
    const fetchNewsData = async () => {
      try {
        const newsRes = await fetchNews({
          variables: { ...input, limit: 4, page: 1 },
          context,
        });
        updateDataState("stockNews", newsRes.data?.getPublicStockNews?.data);
      } catch (err) {
        console.error("News fetch failed", err);
      } finally {
        updateLoadingState("stockNews", false);
      }
    };

    // Fetch Similar Stocks
    const fetchSimilarData = async () => {
      try {
        const similarRes = await fetchSimilar({
          variables: { ...input, limit: 3 },
          context,
        });
        updateDataState(
          "similarStocks",
          similarRes.data?.getPublicStockToSimilarStock?.data,
        );
      } catch (err) {
        console.error("Similar stocks fetch failed", err);
      } finally {
        updateLoadingState("similarStocks", false);
      }
    };

    // Fetch AI Response
    const fetchAiData = async () => {
      try {
        const aiRes = await fetchAi({
          variables: input,
          context,
        });
        updateDataState("openAiResponse", aiRes.data?.getPublicOpenAiResponse);
      } catch (err) {
        console.error("AI response fetch failed", err);
      } finally {
        updateLoadingState("openAiResponse", false);
      }
    };

    // Fetch Projection
    const fetchProjectionData = async () => {
      try {
        const projectionRes = await fetchProjection({
          variables: input,
          context,
        });
        updateDataState("projection", projectionRes.data?.getPublicProjection);
      } catch (err) {
        console.error("Projection fetch failed", err);
      } finally {
        updateLoadingState("projection", false);
      }
    };

    // Fetch Buying Opportunities
    const fetchOpportunitiesData = async () => {
      try {
        const oppRes = await fetchOpportunities({
          context,
        });
        updateDataState(
          "buyingOpportunitiesData",
          oppRes.data?.getPublicBuyingOpportunities,
        );
      } catch (err) {
        console.error("Buying opportunities fetch failed", err);
      } finally {
        updateLoadingState("buyingOpportunities", false);
      }
    };

    //Fetch Banners
    const fetchBannersData = async () => {
      try {
        const bannerRes = await fetchBanners();
        updateDataState("addBanners", bannerRes?.data?.getRandomBanners);
      } catch (err) {
        console.error("Banner fetch failed", err);
      } finally {
        updateLoadingState("addBanners", false);
      }
    };

    // Execute all fetches independently
    fetchNewsData();
    fetchSimilarData();
    fetchAiData();
    fetchProjectionData();
    fetchOpportunitiesData();
    fetchBannersData();
  }, [name, exchange]);

  const handleBack = () => {
    setOpen && setOpen(false);
    router.back();
  };

  const renderFormattedCurrency = (value, currency) => (
    <>
      {CurrencySymbol(currency)}
      <NumericFormat
        value={value}
        displayType="text"
        thousandSeparator
        decimalScale={0}
        fixedDecimalScale
      />
    </>
  );

  // Loading component for individual sections
  const SectionLoader = () => (
    <Row gutter={[24, 24]} className="_calcTab-row w-100 mb-24">
      <Col md={{ span: 24 }}>
        <Spinner />
      </Col>
    </Row>
  );

  return (
    <Fragment>
      <Modal
        open={errorModal}
        className="custom-modal edit-modal"
        centered
        title="You Have Viewed 5 Stocks!"
        onCancel={() => {
          setErrorModal(false);
          router.push(ROUTE?.HOME);
        }}
        okText="Join Tykr For Free"
        onOk={() => {
          setErrorModal(false);
          router.push(URLS?.TYKR_SIGNUP);
        }}
        cancelButtonProps={{ style: { display: "none" } }}
        okButtonProps={{ style: { width: "100%" } }}
      >
        <p>To view unlimited stocks, you can join Tykr for free.</p>
      </Modal>
      {!stockData ? (
        <Spinner />
      ) : (
        <div className="search-container">
          <div className="back-button d-flex">
            <div onClick={handleBack} className="cursor-pointer">
              <LeftOutlined />
              <span className="ml-4 fs-16">Back</span>
            </div>
          </div>
          <Header
            stockDetail={stockData}
            title={stockData?.isEtf ? "fund" : "stock"}
          />

          {stockData?.hasLimitedData ?
            <div className="top-warning-limited-data">
              <div className="warning-menu">
                <ExclamationCircleOutlined
                  style={{
                    color: "#FAAD14",
                    fontSize: "18px",
                    marginRight: 5,
                  }}
                />
                <p className="alert-error " style={{ display: "unset" }}>
                  <span className="warning-title">
                    {" "}
                    {(stockData?.isEtf || stockData?.isFund)
                      ? "Warning: Limited data. Ratings coming soon."
                      : "Warning: Stocks coming soon."}
                  </span>
                </p>
              </div>
              <p className="alert-error-limited-data">
                {
                  TOOLTIPS[
                  (stockData?.isEtf || stockData?.isFund) ? "Limited ETF data" : "Limited data"
                  ]
                }
              </p>
            </div> : <SummaryChart stockData={stockData} />}
          <div className="mt-24">
            <Row gutter={[24, 24]}>
              {CONTENT_DATA?.map((item, index) => (
                <Col key={index} xs={24} sm={24} md={12} lg={12}>
                  <Calculation item={item} />
                </Col>
              ))}
            </Row>
          </div>
          {stockData?.url && (
            <VideoModule
              title={`Is ${stockData?.companyName} (${tickerNameFormat(stockData?.ticker)}) ${stockData?.isEtf ? "fund" : "stock"} a good buy?`}
              url={stockData?.url}
            />
          )}
          {stockAllData?.stockNews?.length > 0 && (
            <div className="stock-container mt-24">
              <h2 className="why-title mt-0 mb-0 h4-style">{`${stockData?.companyName} (${tickerNameFormat(stockData?.ticker)}) Latest news`}</h2>
              <div className="mt-24">
                {loadingStates?.stockNews ? (
                  SectionLoader()
                ) : (
                  <Row gutter={[24, 24]} className="_calcTab-row">
                    {stockAllData?.stockNews?.map((item, index) => {
                      return (
                        <Col
                          md={{ span: 24 }}
                          key={index}
                          className="news-data-col mt-0"
                        >
                          <News stockNews={item?.news} index={index} />
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>
            </div>
          )}
          {/* Add block ad */}
          {stockAllData?.addBanners?.[0] && (
            <div className="mt-24 stock-container p-0 markdown-content">
              <AdBlock
                size="banner"
                position="top"
                className="mb-6"
                addBanners={stockAllData?.addBanners?.[0]}
              />
            </div>
          )}
          <CompanyHistory
            companyHistoryData={stockAllData?.openAiResponse?.companyHistory}
            title={`${stockData?.companyName} (${tickerNameFormat(stockData?.ticker)}) ${stockData?.isEtf ? "fund" : "Company"} History`}
            loading={loadingStates?.openAiResponse}
          />
          {(stockAllData?.projection?.tykr ||
            stockAllData?.projection?.ruleOne ||
            stockAllData?.projection?.benGraham ||
            stockAllData?.projection?.discountedCashFlow) && (
              <Projection
                stockDetails={stockData}
                projection={stockAllData?.projection}
              />
            )}

          {/* Add block ad */}
          {stockAllData?.addBanners?.[1] && (
            <div className="mt-24 stock-container p-0 markdown-content">
              <AdBlock
                size="banner"
                position="top"
                className="mb-6"
                addBanners={stockAllData?.addBanners?.[1]}
              />
            </div>
          )}
          <CompanyHistory
            companyHistoryData={stockAllData?.openAiResponse?.isGoodStockToBuy}
            loading={loadingStates?.openAiResponse}
            title={`Is ${stockData?.companyName} (${tickerNameFormat(stockData?.ticker)}) ${stockData?.isEtf ? "fund" : "stock"} a good buy?`}
            note={true}
          />
          <div className="stock-container mt-24">
            <h2 className="why-title mt-0 mb-0 h4-style">
              Don’t miss out on lucrative buying opportunities!
            </h2>
            <p className="mt-16 company-dec">
              If you feel like you&apos;re missing out and when to make money in
              the stock market, now is your chance to get ahead of the trend.
            </p>
            <p className="mt-16 company-dec">
              Tykr&apos;s Watchlist feature proactively sends you emails when
              stocks turn to &quot;On Sale&quot; which is a potential buying
              opportunity. This intelligent notification system ensures
              you&apos;re always one step ahead, ready to capitalize on
              strategic investments.
            </p>
            <p className="mt-16 company-dec">
              Each of these stocks turned to &quot;On Sale&quot; at these points
              in time.
            </p>
            <div className="mt-16 mb-16">
              {loadingStates?.buyingOpportunities ? (
                SectionLoader()
              ) : (
                <Row gutter={[16, 16]} className="_calcTab-row">
                  {stockAllData?.buyingOpportunitiesData?.map((stock) => {
                    const {
                      _id,
                      ticker,
                      exchangeShortName,
                      image,
                      companyName,
                      currency,
                      investment,
                      currentValue,
                      year,
                    } = stock;
                    return (
                      <Col key={_id} xs={24} sm={24} md={24} lg={24}>
                        <div className="d-flex align-items-center fs-16 gap-2">
                          <div
                            className="d-flex align-items-center gap-2 cursor-pointer"
                            onClick={() =>
                              router.push(
                                `/${ticker?.toLowerCase()}/${exchangeShortName?.toLowerCase()}`,
                              )
                            }
                          >
                            <img
                              src={image}
                              alt={companyName}
                              width={24}
                              height={24}
                            />
                            <b>
                              {companyName} ({tickerNameFormat(ticker)}):
                            </b>
                          </div>
                          <p>
                            If you would have invested{" "}
                            <b>
                              {renderFormattedCurrency(investment, currency)}
                            </b>{" "}
                            in <b>{year}</b>, your investment would be around{" "}
                            <b>
                              {renderFormattedCurrency(currentValue, currency)}
                            </b>{" "}
                            today.
                          </p>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </div>
            {!xToken && (
              <div
                onClick={() => {
                  window.open(URLS.TYKR_SIGNUP, "_blank");
                }}
                className="join-tykr"
              >
                Join Tykr For Free
              </div>
            )}
          </div>
          {stockAllData?.similarStocks?.length > 0 && (
            <div className="stock-container mt-24">
              <h2 className="why-title mt-0 mb-0 h4-style">
                Similar {stockData?.isEtf ? "funds" : "stocks"}
              </h2>
              <div className="mt-16">
                {loadingStates?.similarStocks ? (
                  SectionLoader()
                ) : (
                  <Row gutter={[24, 24]} className="_calcTab-row">
                    {stockAllData?.similarStocks?.map((stock) => (
                      <Col key={stock.id} xs={24} sm={24} md={12} lg={8}>
                        <StockCard
                          key={stock.id}
                          stocks={stock}
                          router={router}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default StockClient;
